import type { LTWHP } from "../types";

const sort = (rects: Array<LTWHP>) =>
    rects.sort((A, B) => {
        const top = (A.pageNumber || 0) * A.top - (B.pageNumber || 0) * B.top;

        if (top === 0) {
            return A.left - B.left;
        }

        return top;
    });

const overlaps = (A: LTWHP, B: LTWHP) =>
    A.pageNumber === B.pageNumber &&
    A.left <= B.left &&
    B.left <= A.left + A.width;

const sameLine = (A: LTWHP, B: LTWHP, yMargin = 5) =>
    A.pageNumber === B.pageNumber &&
    Math.abs(A.top - B.top) < yMargin &&
    Math.abs(A.height - B.height) < yMargin;

const inside = (A: LTWHP, B: LTWHP) =>
    A.pageNumber === B.pageNumber &&
    A.top > B.top &&
    A.left > B.left &&
    A.top + A.height < B.top + B.height &&
    A.left + A.width < B.left + B.width;

const nextTo = (A: LTWHP, B: LTWHP, xMargin = 10) => {
    const Aright = A.left + A.width;
    const Bright = B.left + B.width;

    return (
        A.pageNumber === B.pageNumber &&
        A.left <= B.left &&
        Aright <= Bright &&
        B.left - Aright <= xMargin
    );
};

const extendWidth = (A: LTWHP, B: LTWHP): LTWHP => ({
    ...A,
    width: Math.max(B.width - A.left + B.left, A.width),
});

export const optimizeClientRects = (
    clientRects: Array<LTWHP>,
): Array<LTWHP> => {
    const rects = sort(clientRects);

    const toRemove = new Set();

    const firstPass = rects.filter((rect) =>
        rects.every((otherRect) => !inside(rect, otherRect)),
    );

    let currentRects = [...firstPass];

    for (let passCount = 0; passCount <= 2; passCount += 1) {
        const rectsToProcess = [...currentRects];
        currentRects = rectsToProcess.map((A, indexA) => {
            if (toRemove.has(A)) {
                return A;
            }
            const updatedA = rectsToProcess.reduce((acc, B, indexB) => {
                if (indexA === indexB || toRemove.has(B)) {
                    return acc;
                }

                if (!sameLine(acc, B)) {
                    return acc;
                }

                if (overlaps(acc, B)) {
                    const extended = extendWidth(acc, B);
                    toRemove.add(B);
                    return {
                        ...extended,
                        height: Math.max(extended.height, B.height),
                    };
                }

                if (nextTo(acc, B)) {
                    toRemove.add(B);
                    return extendWidth(acc, B);
                }

                return acc;
            }, A);
            return updatedA;
        });
    }

    return currentRects.filter((rect) => !toRemove.has(rect));
};